<template>
  <div>
    <v-app v-if="localeIsSet && !mustConnect">
      <TheHeader
        @synchronize="synchronize"
      />
      <v-main>
        <vue-page-transition name="fade-in-up">
          <router-view
            ref="view"
            :is-fetching="isFetching"
            :is-synchronizing="isSynchronizing"
            @synchronize="synchronize"
          />
        </vue-page-transition>
      </v-main>
      <TheFooter/>
    </v-app>
    <v-snackbar
      tile
      :right="!isMobile"
      v-model="showOfflineSnackbar"
      timeout="6000"
    >
      <div class="sharing-snackbar__title">
        <v-icon>{{ icons.info }}</v-icon>
        <strong>{{ $t('messages.is-offline') }}</strong>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
  import { mdiInformationOutline } from '@mdi/js'
  import { localstorageStore } from './plugins/localforage'

  import TheHeader from './components/layout/header/TheHeader.vue'
  import TheFooter from './components/layout/footer/TheFooter.vue'

  export default {
    name: 'App',

    components: {
      TheHeader,
      TheFooter,
    },

    data() {
      return {
        localeIsSet: false,
        isANotFound: false,
        appIsMounted: false,
        mustSynchronize: false,
        isSynchronizing: false,
        isFetching: false,
        mustConnect: false,
        showOfflineSnackbar: false,
        icons: {
          info: mdiInformationOutline,
        },
      }
    },

    watch: {
      '$route'() {
        if (!this.appIsMounted) this.mountApp()
      },
    },

    computed: {
      isOnline() {
        return this.$store.state.isOnline
      },

      isMobile() {
        return this.$vuetify.breakpoint.name === 'xs'
      },
    },

    methods: {
      async mountApp() {
        const isOnline = await this.$store.dispatch('checkIfIsOnline')
        if (!isOnline) this.showOfflineSnackbar = true

        const mountMapZoom = this.$store.dispatch('getOfflineMapZoom')
        const mountLocale = this.mountLocale()
        const mountFavorites = this.mountFavorites()
        const mountSynchronization = this.mountSynchronization()

        await Promise.all([mountMapZoom, mountLocale, mountFavorites, mountSynchronization])

        const mountFetching = this.mountFetching()
        const mountMenus = this.mountMenus()

        await Promise.all([mountFetching, mountMenus])

        await this.mountSlugs(this.$store.state.objects.pois)

        this.appIsMounted = true

        if (this.$refs['view'] && this.$refs['view'].displayPois) this.$refs['view'].displayPois()
      },

      async mountLocale() {
        let locale = this.$route.params.locale

        if (locale) {
          if (this.$store.state.locales.includes(locale)) {
            await this.setLocale(locale)

            return
          }

          this.isANotFound = true
        }

        locale = await localstorageStore.getItem('locale')

        if (locale) {
          await this.setLocale(locale)
          return
        }

        locale = navigator.language.split('-')[0] || 'en'

        await this.setLocale(locale)

        return
      },

      async mountFavorites() {
        await this.$store.dispatch('fetchFavoritesFromLocal')
      },

      async mountSynchronization() {
        const { dispatch } = this.$store
        this.isFetching = true

        const mustSynchronize = await dispatch('checkIfMustSynchronize')

        this.mustSynchronize = mustSynchronize
      },

      async mountFetching() {
        if (this.mustSynchronize) return await this.synchronize()

        return await this.fetchPois()
      },

      async mountMenus() {
        if (this.$store.state.isOnline) await this.$store.dispatch('fetchMenusFromApi')
      },

      async mountSlugs(pois) {
        const { dispatch, commit } = this.$store

        const slugsMapping = await dispatch('getMappingFromPois', pois)

        await commit('setSlugsMapping', slugsMapping)
      },

      async fetchPois() {
        try {
          this.isFetching = true
          await this.$store.dispatch('fetchPoisFromLocal')

          this.isFetching = false
        } catch (err) {
          console.log('err', err)
        }
      },

      async synchronize() {
        this.isFetching = true
        this.isSynchronizing = true

        if (this.$refs['view'].synchronize) this.$refs['view'].synchronize()

        try {
          await this.$store.dispatch('fetchPoisFromApi')

          this.isSynchronizing = false
          this.isFetching = false

          if (this.$refs['view'] && this.$refs['view'].displayPois) this.$refs['view'].displayPois()
        } catch (err) {
          this.isFetching = false
          this.isSynchronizing = false
          console.log('err', err)
        }
      },

      async setLocale(locale) {
        const { name: routeName, params: routeParams } = this.$route
        if (routeName === 'HomePage' && !routeParams.locale) {
          await this.$router.push({
            name: 'HomePage',
            params: { locale }
          })
        }

        const { commit } = this.$store

        this.$i18n.locale = locale

        commit('setLocale', locale)
        await localstorageStore.setItem('locale', locale)

        if (!this.isANotFound) return this.localeIsSet = true

        return await this.$router.push({
          name: 'NotFoundPage',
          params: {
            path: `${locale}${this.$route.path}`,
          },
        })
      },
    },
  }
</script>

<style lang="sass">
  @import "@/assets/styles/_config.sass"

  @font-face
    font-family: EuclidBold
    src: url("./assets/fonts/EuclidBold.ttf")

  @font-face
    font-family: EuclidMedium
    src: url("./assets/fonts/EuclidMedium.ttf")

  @font-face
    font-family: EuclidRegular
    src: url("./assets/fonts/EuclidRegular.ttf")

  *
    font-family: "EuclidRegular", "sans-serif"

  h1, h2, h3
    font-family: "EuclidBold", "sans-serif"

  .container
    max-width: 960px

  body
    width: 100%

  html
    scroll-behavior: smooth

  .menu-container__item-title span,
  .menu-container__item-title a
    font-family: "EuclidBold"

  .menu-container__subitems-items-link
    font-family: "EuclidMedium"

  main.v-main
    min-height: 1000px

  @media (min-width: 960px)
    .container
      width: 100%
</style>

<style lang="sass" scoped>
  .v-icon
    margin-right: 5px
</style>
